import styled from '@emotion/styled';
import { ObjectId } from '@racemap/sdk/schema/base';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { validateMongoId } from '@racemap/utilities/functions/validation';
import { type FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { animated } from 'react-spring';
import { BottomSheet } from 'react-spring-bottom-sheet';
import 'react-spring-bottom-sheet/dist/style.css';
import { Modal } from 'antd';
import { useQuery } from '../../lib/customHooks';
import { useStore } from '../../store/reducers';
import { SearchBar } from '../BasicComponents/SearchBar/SearchBar';
import { IconFilter } from '../Icon';
import { ActionsDropDown } from './ActionsDropDown';
import { CreateTrackerButton } from './CreateTracker/CreateTrackerButton';
import { FilterTree } from './Filter/FilterTree';
import { TrackerInfos } from './Layout/TrackerInfos';
import { TotalTrackerTable } from './Tables/TotalTrackerTable';

export const TopBarHeight = '25px';

export const TrackerManagerMobile: FC = () => {
  const searchValue = useStore((s) => s.trackers.searchValue);
  const { setSearchValue } = useStore((s) => s.trackers.actions);
  const inspectedTrackerId = useStore((s) => s.trackers.inspectedTrackerId);
  const inspectedTracker = useStore((s) =>
    s.trackers.items.get(inspectedTrackerId?.toHexString() || ''),
  );
  const { changeInspectedTrackerId } = useStore((s) => s.trackers.actions);
  const [infoOpen, setInfoOpen] = useState(inspectedTracker != null);
  const [isModalFilterTreeOpen, setIsModalFilterTreeOpen] = useState(false);
  const query = useQuery();
  const navigate = useNavigate();

  useEffect(() => {
    if (inspectedTrackerId == null && infoOpen === true) {
      setInfoOpen(false);
    } else if (infoOpen === false && inspectedTrackerId != null) {
      setInfoOpen(true);
    }
  }, [inspectedTrackerId]);

  useEffect(() => {
    const inspectedTrackerId = query.get('inspectedTracker');
    if (validateMongoId(inspectedTrackerId)) {
      changeInspectedTrackerId(new ObjectId(inspectedTrackerId));
    } else {
      changeInspectedTrackerId(null);
    }
  }, [query]);

  const onDismiss = () => {
    setInfoOpen(false);
    changeInspectedTrackerId(null);
    navigate({ pathname: '/admin/trackers', search: '' });
  };

  const maxBottomSheetHeight = window.innerHeight * 0.75;

  return (
    <Container>
      <Main>
        <TopBar>
          <CreateTrackerButton />
          <TableOptionsContainer>
            <SearchBar value={searchValue} onChange={setSearchValue} size="lg" />
          </TableOptionsContainer>
          <ButtonContainer>
            <Icon onClick={() => setIsModalFilterTreeOpen(true)} />
            <ActionsDropDown />
          </ButtonContainer>
        </TopBar>
        <TotalTrackerTable onMoveToSample={() => {}} onRemoveFromSample={() => {}} />
      </Main>
      <BottomSheet
        onDismiss={onDismiss}
        onDragExit={() => changeInspectedTrackerId(null)}
        open={infoOpen}
        blocking={false}
        maxHeight={maxBottomSheetHeight}
        snapPoints={({ maxHeight }) => [maxHeight * 0.5, maxHeight]}
        defaultSnap={({ maxHeight }) => maxHeight * 0.5}
      >
        <TrackerInfos />
      </BottomSheet>
      <Modal
        title="Filter Tree"
        open={isModalFilterTreeOpen}
        onCancel={() => setIsModalFilterTreeOpen(false)}
        footer={null}
      >
        <FilterTreeWrapper>
          <FilterTree />
        </FilterTreeWrapper>
      </Modal>
    </Container>
  );
};

const Container = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`;

const Main = styled.div`
  margin-top: 10px;
  height: calc(100% - 20px);
  position: relative;
  bottom: 0;
  right: 0;
  left: 0;
  background: white;
  border: solid 1px ${RacemapColors.LightGray};
  border-radius: 0px;
`;

const TableOptionsContainer = styled.div`
  width: clamp(150px, 300px, 400px);

  .input-group,
  .input-group-append,
  .form-control:not(textarea) {
    height: 30px;
  }
`;

const TopBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px;
`;

const ButtonContainer = styled(animated.div)`
  display: flex;
  align-items: center;
`;

const Icon = styled(IconFilter)`
  height: 15px;
  padding-left: 8px;
  padding-right: 4px;
`;

const FilterTreeWrapper = styled.div`
  max-height: 600px; 
  overflow-y: auto;
  padding-right: 16px;
`;
